/**
 * FixReview — the core spellcheck review component.
 *
 * Steps through each diagnostic showing a before/after diff.
 * The user clicks Accept, Skip, Accept All, or Done.
 * Decisions are sent to the API so the SDK can poll for completion.
 */

import { useCallback, useState } from "react";
import { Button, Flex, Text } from "@radix-ui/themes";

import type { FixDecision, FixSession } from "../../api/fix";
import { useUpdateFixDecisions } from "../../hooks/useFixSession";
import CodeDiff from "./CodeDiff";

interface FixReviewProps {
  session: FixSession;
}

export default function FixReview({ session }: FixReviewProps) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [decisions, setDecisions] = useState<FixDecision[]>([]);
  const [done, setDone] = useState(false);
  const update = useUpdateFixDecisions(session.id);

  const total = session.diagnostics.length;
  const diag = session.diagnostics[currentIdx];
  const ctx = session.contexts[currentIdx];

  const fileName = diag ? diag.file.split("/").pop() ?? diag.file : "";

  const advance = useCallback(
    (newDecisions: FixDecision[]) => {
      if (currentIdx + 1 >= total) {
        setDone(true);
        update.mutate({ decisions: newDecisions, status: "complete" });
      } else {
        setCurrentIdx((i) => i + 1);
        update.mutate({ decisions: newDecisions, status: "in_progress" });
      }
    },
    [currentIdx, total, update],
  );

  const onAccept = useCallback(() => {
    const next = [...decisions, { index: currentIdx, action: "accept" as const }];
    setDecisions(next);
    advance(next);
  }, [currentIdx, decisions, advance]);

  const onSkip = useCallback(() => {
    const next = [...decisions, { index: currentIdx, action: "skip" as const }];
    setDecisions(next);
    advance(next);
  }, [currentIdx, decisions, advance]);

  const onAcceptAll = useCallback(() => {
    const remaining: FixDecision[] = [];
    for (let i = currentIdx; i < total; i++) {
      remaining.push({ index: i, action: "accept" });
    }
    const next = [...decisions, ...remaining];
    setDecisions(next);
    setDone(true);
    update.mutate({ decisions: next, status: "complete" });
  }, [currentIdx, total, decisions, update]);

  const onDone = useCallback(() => {
    const remaining: FixDecision[] = [];
    for (let i = currentIdx; i < total; i++) {
      remaining.push({ index: i, action: "skip" });
    }
    const next = [...decisions, ...remaining];
    setDecisions(next);
    setDone(true);
    update.mutate({ decisions: next, status: "complete" });
  }, [currentIdx, total, decisions, update]);

  if (done || session.status === "complete") {
    const accepted = (done ? decisions : session.decisions).filter(
      (d) => d.action === "accept",
    ).length;
    const skipped = (done ? decisions : session.decisions).filter(
      (d) => d.action === "skip",
    ).length;

    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <Text size="4" weight="bold" style={{ color: "#569cd6" }}>
            Thread Provenance Check
          </Text>
        </div>
        <div style={{ padding: "40px 24px", textAlign: "center" }}>
          <Text size="5" weight="bold" style={{ color: "#d4d4d4" }}>
            Review Complete
          </Text>
          <div style={{ marginTop: 16, color: "#6a737d", fontSize: 14 }}>
            {accepted > 0 && <div>{accepted} fix{accepted !== 1 ? "es" : ""} accepted</div>}
            {skipped > 0 && <div>{skipped} skipped</div>}
            <div style={{ marginTop: 16 }}>
              Fixes are being applied by the SDK. You can close this tab.
            </div>
          </div>
        </div>
        <div style={privacyStyle}>
          Runs entirely on your machine. No code is sent to any server unless you choose to.
        </div>
      </div>
    );
  }

  if (!diag || !ctx) return null;

  const isStale = ctx.stale;

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <Text size="4" weight="bold" style={{ color: "#569cd6" }}>
          Thread Provenance Check
        </Text>
        <Text size="2" style={{ color: "#6a737d" }}>
          [{currentIdx + 1} of {total}]
        </Text>
      </div>

      <div style={{ padding: "0 24px 16px" }}>
        <CodeDiff context={ctx} fileName={fileName} lineNumber={diag.line} />
      </div>

      <Flex gap="3" px="5" py="3">
        <Button
          variant="solid"
          disabled={isStale}
          onClick={onAccept}
          style={{
            background: isStale ? "#333" : "#2d5a2d",
            cursor: isStale ? "not-allowed" : "pointer",
          }}
        >
          Accept
        </Button>
        <Button variant="soft" color="gray" onClick={onSkip}>
          Skip
        </Button>
        <Button
          variant="soft"
          color="gray"
          disabled={isStale}
          onClick={onAcceptAll}
        >
          Accept All
        </Button>
        <div style={{ flex: 1 }} />
        <Button variant="soft" color="gray" onClick={onDone}>
          Done
        </Button>
      </Flex>

      <div style={privacyStyle}>
        Runs entirely on your machine. No code is sent to any server unless you choose to.
      </div>
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  background: "#1e1e1e",
  minHeight: "100vh",
  color: "#d4d4d4",
  display: "flex",
  flexDirection: "column",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 24px 8px",
};

const privacyStyle: React.CSSProperties = {
  marginTop: "auto",
  padding: "12px 24px",
  background: "#252526",
  color: "#6a737d",
  fontSize: 11,
};
